import {
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  HostListener,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { DomSanitizer, SafeUrl, Title } from "@angular/platform-browser";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";

import { Color } from "@angular-material-components/color-picker";
import { MatDialog } from "@angular/material/dialog";

import { TranslateService } from "@ngx-translate/core";
import { cloneDeep } from "lodash";

import { BehaviorSubject, EMPTY, Observable, of, Subject, Subscription } from "rxjs";
import { catchError, filter, finalize, map, shareReplay, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  AppState,
  loginStateLocaleList,
} from "src/app/store/reducers";

import { ProductFamilyService } from "src/app/shared/services/products/productFamily/productFamily.service";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { FormUtils } from "src/app/shared/utils/formUtils";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { ProductFamilyDTO } from "src/app/models/Products/productDTO";
import {
  Dialog,
  GuardConfirmationDialogData,
} from "src/app/models/dialog/dialog.model";
import { UserLocaleDTO } from "src/app/models/UserDetails/userLocaleDTO";
import { AutocompleteSelectItem } from "src/app/models/selection-list/autocomplete-select-item.model";
import { getColorByBgColor } from "src/app/shared/utils/colorUtils";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { buildDetailedDescription, populateDetailedDescription, setDetailedDescription, setFormAfterDialogClosed } from "src/app/shared/utils/localeDescriptionUtils";

import { CanComponentDeactivate } from "src/app/shared";
import { DescriptionLocaleFormDialogComponent } from "src/app/shared/modules/dialogs/description-locale-form-dialog/description-locale-form-dialog.component";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { ManageFilesDialogComponent } from "src/app/shared/modules/dialogs/manage-files-dialog/manage-files-dialog.component";
import { ManageFileService } from "src/app/shared/services/manage-files/manage-files.service";
import { RolePermissionsService } from "src/app/shared/services/role-permissions/role-permissions.service";

export const FORM_PARAMS = {
  code: "code",
  description: "description",
  detailedDescription: "detailedDescription",
  color: "color",
  imageUrl: "imageUrl",
  parentProductFamily: "parentProductFamily",
};
@Component({
  selector: "app-product-family-detail",
  templateUrl: "./product-family-detail.component.html",
  styleUrls: ["./product-family-detail.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ProductFamilyDetailComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  //Product Family
  isLoading$ = new BehaviorSubject<boolean>(false);
  loadedProductFamily$: Observable<ProductFamilyDTO>;
  created$: Observable<boolean>;

  //Product Families
  isLoadingProductFamily$ = new BehaviorSubject<boolean>(false);
  productFamiliesList$: Observable<ProductFamilyDTO[]>;
  productFamiliesList: AutocompleteSelectItem[] = [];
  searchTimer: number;

  //top-card configuration
  card: any;
  icon: any = "add";

  //Navigation
  currToken: string;
  currentProductFamily: ProductFamilyDTO;

  //Locales
  localesList$: Observable<UserLocaleDTO[]>;
  localesListSub: Subscription;

  //Actions
  creation: boolean = false;
  duplication: boolean = false;

  //Family Objects
  loadedData: string;
  currentData: string;

  //Form
  productFamilyDataForm: FormGroup;

  // Permissions
  public canWrite$: Observable<boolean>;
  canWrite: boolean = false;
  public canReadProducts$: Observable<boolean>;

  //Files
  selectedFiles: string[];
  public selectedFilesChip: { name: string; id: string }[] = [];

  //Thumbnail
  public thumbnail$ = new BehaviorSubject<SafeUrl | null>(null);
  private thumbnailCache: Record<string, SafeUrl> = {};
  private fileThumbnails: Record<string, Observable<SafeUrl>> = {};
  private destroy$ = new Subject<void>();

  //Language
  langSubscription: Subscription;

  // loaded locale
  public locale: string = localStorage.getItem("locale");
  localeDesc: string;

  constructor(
    private translate: TranslateService,
    private fb: FormBuilder,
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private manageFileService: ManageFileService,
    private sanitizer: DomSanitizer,
    private store: Store<AppState>,
    private productFamilyService: ProductFamilyService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    public rolePermissionsService: RolePermissionsService
  ) {
    this.titleService.setTitle(this.translate.instant("ProductFamily.Details"));

    this.localesList$ = this.store.pipe(select(loginStateLocaleList));

    //initialize card configuration
    this.setTopcard();
  }

  ngOnInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "ignore";

    this.route.params.subscribe((params) => {
      const code = params.id;
      this.creation = false;
      this.duplication = false;
      const duplicateCode = localStorage.getItem("duplicate");

      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      if (code && code.length) {
        this.initialiseState(code, false);
      } else if (duplicateCode !== null) {
        this.duplication = true;
        localStorage.removeItem("duplicate");
        this.initialiseState(duplicateCode, true);
      } else {
        this.creation = true;
        this.initialiseState("", false);
      }
    });

    this.createProductFamilyForm();
    this.loadLangChanges();

    this.loadLocales();
  }

  loadLangChanges() {
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.titleService.setTitle(
        this.translate.instant("ProductFamily.Details")
      );
    });
  }

  loadLocales() {
    this.localesListSub = this.localesList$.subscribe((locales) => {
      if (locales) {
        this.localeDesc = locales.find(
          ({ code }) => code === this.locale
        ).description;
        buildDetailedDescription(
          locales,
          this.fb,
          this.productFamilyDataForm,
          FORM_PARAMS.detailedDescription
        );
        this.checkPermissions();
      }
    });
  }

  createProductFamilyForm() {
    this.productFamilyDataForm = this.fb.group({
      [FORM_PARAMS.code]: [{ value: null, disabled: !this.creation && !this.duplication }, [Validators.required]],
      [FORM_PARAMS.description]: [
        null,
        [
          Validators.required,
          FormUtils.noWhitespaceValidator,
          Validators.maxLength(100),
        ],
      ],
      [FORM_PARAMS.detailedDescription]: this.fb.array([]),
      [FORM_PARAMS.color]: [null],
      [FORM_PARAMS.imageUrl]: [null],
      [FORM_PARAMS.parentProductFamily]: [null, [FormUtils.isObjectValidator]],
    });
  }

  initialiseState(code: string, duplicate?: boolean) {
    if (code) {
      this.loadProductFamily(code);
      if (duplicate) {
        this.currToken = "";
      } else {
        this.currToken = code;
      }
    } else {
      this.currToken = "";
      this.setTopcard();
    }
  }

  setTopcard(productFamily?: ProductFamilyDTO) {
    if (productFamily) {
      this.card = {
        avatarUrl: "",
        content: "",
        imageUrl: "",
        current: !this.duplication ? productFamily?.token : "",
        title: !this.duplication
          ? "[" +
          productFamily?.code +
          "] - " +
          (productFamily?.detailedDescription[this.locale] ?? "")
          : "ProductFamily.newProductFamily",
        subtitle: !this.duplication
          ? "ProductFamily.productFamily"
          : "ProductFamily.duplication",
      };
    } else {
      this.card = {
        avatarUrl: "",
        content: "",
        imageUrl: "",
        current: "",
        title: "ProductFamily.newProductFamily",
        subtitle: this.duplication
          ? "ProductFamily.duplication"
          : "ProductFamily.creation",
      };
    }
  }

  checkPermissions(): void {
    this.rolePermissionsService["roles$"]
      .pipe(
        filter((roles) => roles.length > 0),
        take(1),
        switchMap(() =>
          this.rolePermissionsService.hasPermission(
            "PRODUCT_FAMILY_MANAGEMENT",
            "WRITE"
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((canEdit) => {
        this.canWrite = canEdit;
        if (!canEdit) {
          this.disableFormControls(this.productFamilyDataForm);
        }
      });

    this.canWrite$ = this.rolePermissionsService.hasPermission(
      "PRODUCT_FAMILY_MANAGEMENT",
      "WRITE"
    );

    this.canReadProducts$ = this.rolePermissionsService.hasPermission(
      "PRODUCT_MANAGEMENT",
      "READ"
    )
  }

  protected disableFormControls(form: FormGroup): void {
    const fields = [
      FORM_PARAMS.code,
      FORM_PARAMS.description,
      FORM_PARAMS.detailedDescription,
      FORM_PARAMS.color,
      FORM_PARAMS.imageUrl,
      FORM_PARAMS.parentProductFamily,
    ];

    fields.forEach((field) => form.get(field)?.disable());
  }

  validateFirstProductFamilySearch() {
    this.searchProductFamilies("");
  }

  searchProductFamilies(event: KeyboardEvent | string, hasTimer = true): void {
    const searchTerm =
      typeof event === "string"
        ? event
        : (event.target as HTMLInputElement).value;

    clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(
      () => {
        this.isLoadingProductFamily$.next(true);
        this.productFamiliesList$ = this.productFamilyService
          .getProductFamiliesList(null, {
            filterConfig: [{ field: "description" }],
            searchTerms: [searchTerm],
          })
          .pipe(
            map(({ items }) => {
              return items.map((item) => ({
                code: item.code,
                description: item.detailedDescription[this.locale],
              }));
            }),
            catchError((error) => {
              this.snackbarService.show({
                message: this.translate.instant("ProductFamily.loadError"),
                type: SnackbarType.WARNING,
              });
              return EMPTY;
            }),
            finalize(() => this.isLoadingProductFamily$.next(false)),
            shareReplay()
          );
      },
      hasTimer ? 500 : 0
    );
  }

  displayAutocompleteOption(productFamily: ProductFamilyDTO): string {
    return productFamily?.description ?? "";
  }

  openProductFamily() {
    const productFamilyToken =
      this.productFamilyDataForm.controls[FORM_PARAMS.parentProductFamily]
        ?.value?.token;
    if (productFamilyToken) {
      return ["/families/family", productFamilyToken];
    }
  }

  loadProductFamily(code?: string) {
    if (code && code.length) {
      this.loadProductFamilyInfo(code);
    }
  }

  loadProductFamilyInfo(code?: string) {
    this.isLoading$.next(true);
    this.productFamilyService
      .getProductFamily(code)
      .pipe(
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("ProductFamily.loadProductFamily"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe((productFamily) => {
        this.currentProductFamily = cloneDeep(productFamily);

        if (
          (!this.currToken ||
            (this.currToken && this.currToken?.length === 0)) &&
          !this.duplication
        ) {
          this.currToken = this.currentProductFamily.token;
        }

        this.setTopcard(productFamily);
        this.setFormValues(productFamily);
        // this.checkThumbnail();
      });
  }

  setFormValues(productFamily: ProductFamilyDTO) {
    this.productFamilyDataForm.controls.code.setValue(
      productFamily?.code && !this.duplication ? productFamily.code : null
    );
    this.productFamilyDataForm.controls.description.setValue(
      productFamily && !this.duplication
        ? productFamily?.detailedDescription[this.locale]
        : ""
    );
    if (!this.duplication) {
      populateDetailedDescription(
        this.productFamilyDataForm.get("detailedDescription") as FormArray,
        this.currentProductFamily.detailedDescription
      );
    }

    let color = FormUtils.hexToRgb(productFamily?.color);
    this.productFamilyDataForm.controls.color.setValue(
      color && productFamily?.color && !this.duplication
        ? new Color(color.r, color.g, color.b)
        : new Color(0, 0, 0)
    );
    this.productFamilyDataForm.controls.imageUrl.setValue(
      productFamily && productFamily?.imageUrl && !this.duplication
        ? productFamily?.imageUrl
        : ""
    );
    this.productFamilyDataForm.controls.parentProductFamily.setValue(
      productFamily?.parentProductFamily
        ? {
            token: productFamily.parentProductFamily.token,
            code: productFamily.parentProductFamily.code,
            description:
              productFamily.parentProductFamily.detailedDescription[
                this.locale
              ],
          }
        : null
    );

    if (productFamily.imageUrl) {
      this.loadImageFileName(productFamily.imageUrl);
    }
  }

  clearFormControl(form: FormGroup, productGroupControl: string): void {
    form.controls[productGroupControl].setValue(null);
    form.markAsDirty();
    this.thumbnail$.next(false);
  }
  
  checkThumbnail() {
    const imageId = this.productFamilyDataForm.get(FORM_PARAMS.imageUrl)?.value;
    if (imageId) {
      this.loadThumbnail(imageId);
    }
    this.productFamilyDataForm.get(FORM_PARAMS.imageUrl)?.valueChanges
      .pipe(
        filter(Boolean),
        takeUntil(this.destroy$)
      )
      .subscribe((imageId: string | null) => {
        if (imageId) {
          this.loadThumbnail(imageId);
        } else {
          this.thumbnail$.next(null)
        }
      });
  }

  private loadImageFileName(token: string): void {
    this.manageFileService.fileDetails(token).subscribe({
      next: (file) => {
        this.selectedFilesChip = [{ name: file.name, id: file.id }];
      },
      error: () => {
        this.selectedFilesChip = [{ name: token, id: token }];
      }
    });
  }

  private loadThumbnail(fileId: string): void {
    this.getThumbnail(fileId).subscribe((thumb) => this.thumbnail$.next(thumb));
  }

  private getThumbnail(fileId: string): Observable<SafeUrl> {
    if (this.thumbnailCache[fileId]) {
      return of(this.thumbnailCache[fileId]);
    }

    if (!this.fileThumbnails[fileId]) {
      this.fileThumbnails[fileId] = this.manageFileService.getFileThumbnail(fileId).pipe(
        map((blob) => {
          const url = URL.createObjectURL(blob);
          const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
          this.thumbnailCache[fileId] = safeUrl;
          return safeUrl;
        })
      );
    }

    return this.fileThumbnails[fileId];
  }

  public openManageFilesDialog(): void {
    const currentImageUrl = this.productFamilyDataForm.get(FORM_PARAMS.imageUrl)?.value;

    this.dialog.open(ManageFilesDialogComponent, {
      data: {
        treatedDtoSelection: currentImageUrl ? [currentImageUrl] : [],
        allowMultiple: false,
        fileType: 'image'
      },
      width: '85%',
      maxHeight: '100vh',
      panelClass: 'search-site-dialog',
      autoFocus: false,
    }).afterClosed().subscribe((fileSubmission: { submitted: boolean, selectedFiles: any[] }) => {
      const { submitted, selectedFiles } = fileSubmission;

      if (!submitted) {
        return;
      }

      if (selectedFiles && selectedFiles.length > 0) {
        const selectedFile = selectedFiles[0];

        const imageUrl = selectedFile?.id ?? selectedFile;

        this.productFamilyDataForm.get(FORM_PARAMS.imageUrl)?.setValue(imageUrl);
        this.productFamilyDataForm.get(FORM_PARAMS.imageUrl)?.markAsDirty();
        this.productFamilyDataForm.get(FORM_PARAMS.imageUrl)?.markAsTouched();

        this.selectedFilesChip = [
          typeof selectedFile === 'object'
            ? selectedFile
            : { name: selectedFile, id: selectedFile }
        ];
      } else {
        const imageControl = this.productFamilyDataForm.get(FORM_PARAMS.imageUrl);
        imageControl?.setValue(null);
        imageControl?.markAsDirty();
        imageControl?.markAsTouched();

        this.selectedFilesChip = [];
        this.thumbnail$.next(null);
      }
    });
  }

  getColorPickerBgColor(color: Color): string {
    if (color) {
      return getColorByBgColor(color.hex);
    }
  }

  openDescriptionLocaleFormDialog() {
    const detailedDescriptionBackup = [
      ...this.productFamilyDataForm.get("detailedDescription").value,
    ];
    const dialogData = {
      data: {
        parentForm: this.productFamilyDataForm,
        formParam: FORM_PARAMS.detailedDescription,
      },
      width: "400px",
    };
    const dialogRef = this.dialog.open(
      DescriptionLocaleFormDialogComponent,
      dialogData
    );
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        setFormAfterDialogClosed(
          this.locale,
          this.productFamilyDataForm,
          FORM_PARAMS.detailedDescription,
          FORM_PARAMS.description
        );
      } else {
        this.productFamilyDataForm
          .get("detailedDescription")
          .setValue(detailedDescriptionBackup);
      }
    });
  }

  onSubmit() {
    if (
      this.productFamilyDataForm.pristine &&
      !this.creation &&
      !this.duplication
    ) {
      this.snackbarService.show({
        message: this.translate.instant("ProductFamily.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    if (!this.productFamilyDataForm.valid) {
      this.productFamilyDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("ProductFamily.familyFormInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    let productFamily = {
      code: this.productFamilyDataForm.controls.code.value ?? null,
      description:
        this.productFamilyDataForm.controls.description.value ?? null,
      detailedDescription: setDetailedDescription(
        this.productFamilyDataForm,
        FORM_PARAMS.detailedDescription,
        FORM_PARAMS.description,
        this.locale
      ),
      color: this.productFamilyDataForm.controls.color.value
        ? this.productFamilyDataForm.controls.color.value?.hex
        : "",
      imageUrl: this.productFamilyDataForm.controls.imageUrl.value
        ? this.productFamilyDataForm.controls.imageUrl.value
        : "",
      parentProductFamilyCode: this.productFamilyDataForm.controls
        .parentProductFamily.value
        ? this.productFamilyDataForm.controls.parentProductFamily.value.code
        : null,
    };

    if (!this.currToken.length) {
      this.createProductFamily(productFamily);
    } else {
      this.updateProductFamily(productFamily);
    }
  }

  createProductFamily(productFamily: ProductFamilyDTO) {
    this.isLoading$.next(true);
    this.productFamilyService
      .createProductFamily(productFamily)
      .pipe(
        tap((createdProductFamily) => {
          this.snackbarService.show({
            message: this.translate.instant(
              "ProductFamily.CreateProductFamily"
            ),
            type: SnackbarType.SUCCESS,
          });

          this.creation = false;
          this.duplication = false;

          this.currentProductFamily = cloneDeep(createdProductFamily);
          this.currToken = this.currentProductFamily.token;

          this.setTopcard(createdProductFamily);
          this.setFormValues(createdProductFamily);

        this.productFamilyDataForm.controls.code.disable();

        this.productFamilyDataForm.markAsUntouched();
        this.productFamilyDataForm.markAsPristine();
      }),
      catchError((error) => {
        this.snackbarService.show({
          message: this.translate.instant(error) ?? this.translate.instant("ProductFamily.errorCreateProductFamily"),
          type: SnackbarType.ERROR,
        });
        return EMPTY;
      }),
      finalize(() => this.isLoading$.next(false))
    ).subscribe();
  }

  updateProductFamily(productFamily: ProductFamilyDTO) {
    this.isLoading$.next(true);
    this.productFamilyService
      .updateProductFamily(productFamily, this.currToken)
      .pipe(
        tap((updatedProductFamily) => {
          this.snackbarService.show({
            message: this.translate.instant(
              "ProductFamily.UpdateProductFamily"
            ),
            type: SnackbarType.SUCCESS,
          });

          this.currentProductFamily = cloneDeep(updatedProductFamily);

          this.setTopcard(updatedProductFamily);
          this.setFormValues(updatedProductFamily);

          this.productFamilyDataForm.markAsUntouched();
          this.productFamilyDataForm.markAsPristine();
        }),
        catchError((error) => {
          this.snackbarService.show({
            message:
              this.translate.instant(error) ??
              this.translate.instant("ProductFamily.errorUpdateProductFamily"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  back() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.location.back();
  }

  refresh() {
    if (
      isAuthenticated(this.router.url) &&
      !this.productFamilyDataForm.pristine
    ) {
      this.confirmDialog(this.dialog, true);
      return;
    } else {
      this.reload();
    }
  }

  reload() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["families/family", this.currToken]);
  }

  duplicate() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    localStorage.setItem("duplicate", this.currToken);
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["families/family", {}]);
  }

  new() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["/families/family"]);
    this.creation = true;
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (
      !isAuthenticated(this.router.url) ||
      this.productFamilyDataForm.pristine
    ) {
      return true;
    } else {
      return false;
    }
  }

  confirmDialog(dialog: any, refresh = false): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    if (!refresh) {
      return dialogRef.afterClosed();
    }
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.reload();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
    if (this.localesListSub) {
      this.localesListSub.unsubscribe();
    }
  }
}
